import { useEffect, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import {
  DataPacket_Kind,
  LocalParticipant,
  RemoteParticipant,
} from "livekit-client";
import { UserState } from "../types";

// Tracks every speaker participant's language (keyed by LiveKit sid, matching
// how the translator keys "autodetect_source_language" messages) plus which
// one is currently dominant, and returns that speaker's language. With a
// Recall-backed Teams meeting there can be several simultaneous "speaker"
// participants - one per Teams attendee - so "the dominant one" has to come
// from an explicit signal (the bridge's "dominant_speaker_changed" message)
// rather than being inferred from "the only speaker participant," which was
// the old ACS-bot-era assumption.
export const useDominantSpeakerLanguage = () => {
  const [languageBySid, setLanguageBySid] = useState<Record<string, string>>(
    {},
  );
  const [dominantSid, setDominantSid] = useState<string>();
  const room = useRoomContext();

  useEffect(() => {
    const onParticipantMetadataChanged = (
      _: string | undefined,
      participant: LocalParticipant | RemoteParticipant,
    ) => {
      if (!participant.metadata) {
        return;
      }

      const metadata = JSON.parse(participant.metadata) as {
        participant_type: UserState["metadata"]["participantType"];
        language: string;
      };
      if (metadata.participant_type !== "speaker") {
        return;
      }

      if (metadata.language === "autodetect") {
        return;
      }

      setLanguageBySid((prev) => ({
        ...prev,
        [participant.sid]: metadata.language,
      }));
    };

    room.on("participantMetadataChanged", onParticipantMetadataChanged);

    return () => {
      room.off("participantMetadataChanged", onParticipantMetadataChanged);
    };
  }, [room]);

  useEffect(() => {
    const decoder = new TextDecoder();
    const onDataReceived = async (
      payload: Uint8Array,
      _?: RemoteParticipant,
      __?: DataPacket_Kind,
      topic?: string,
    ) => {
      if (topic === "autodetect_source_language") {
        const event = JSON.parse(decoder.decode(payload)) as {
          participant_id: string;
          language: string;
        };
        setLanguageBySid((prev) => ({
          ...prev,
          [event.participant_id]: event.language,
        }));
        return;
      }

      if (topic === "dominant_speaker_changed") {
        const event = JSON.parse(decoder.decode(payload)) as { sid: string };
        setDominantSid(event.sid);
      }
    };
    room.on("dataReceived", onDataReceived);

    return () => {
      room.off("dataReceived", onDataReceived);
    };
  }, [room]);

  return dominantSid ? languageBySid[dominantSid] : undefined;
};
